import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Alert,
  Form,
  FormGroup,
  Label,
} from "reactstrap";
import swal from "sweetalert";
import { FaPlus, FaFilter, FaPencilAlt } from "react-icons/fa";
import { ImDownload } from "react-icons/im";

/* ====== YOUR UTILITIES / APIS (keep these imports pointing to your project) ====== */
import { CheckPermission } from "../../import/CheckPermission"; // <- same as WarehouseList
import { exportDataToPDF } from "../../import/Downloader"; // <- same as WarehouseList
import {
  Get_RoleList,
  _Delete,
  _Get,
  _GetList,
  _PostSave,
} from "../../import/ApiEndPoint/ApiCalling";
import {
  Create_Department,
  Delete_Department,
  List_Department,
  Role_list_by_Master,
} from "../../import/ApiEndPoint/Api";
import SuperAdminUI from "../../import/SuperAdminUI"; // <- adjust if your path differs

/* ====== tiny page-scoped CSS (identical style language to WarehouseList) ====== */
const injectOnce = () => {
  if (document.getElementById("roles-inline-css")) return;
  const style = document.createElement("style");
  style.id = "roles-inline-css";
  style.innerHTML = `
    .rl-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px;
    }
    .rl-table-wrap{
      width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff;
    }
    .rl-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:0; }
    .rl-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .rl-table tbody td{
      padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center;
    }
    .rl-table tbody tr:hover{ background:#f9fbff; }
    .action-chips{ display:flex; justify-content:center; align-items:center; gap:8px }
    .chip{ display:inline-grid; place-items:center; width:28px; height:28px; border-radius:16px; }
    .chip-edit{ background:#d46f10; }
    .rl-form-card{ border:1px solid #e5e7eb; border-radius:14px; background:#fff; margin-top:10px; box-shadow:0 6px 16px rgba(0,0,0,.06);}
    .rl-form .form-group{ margin-bottom:8px; }
    .rl-form label{ font-size:12px; margin-bottom:4px; }
    .rl-form input, .rl-form textarea{ font-size:12px; height:34px; }
  `;
  document.head.appendChild(style);
};

/* ====== helpers ====== */
const toStr = (v) => (v === null || v === undefined ? "" : String(v));

export default function RoleListMaster() {
  const navigate = useNavigate();
  useEffect(() => injectOnce(), []);

  /* ---------------- state ---------------- */
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  // permissions (same shape you used)
  const [permissions, setPermissions] = useState({
    View: true,
    Create: true,
    Edit: true,
    Delete: false,
    Download: true,
  });

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");

  // Master controls
  const [isMaster, setIsMaster] = useState(false);
  const [userData, setUserData] = useState({});

  // Department Modal
  const [deptModal, setDeptModal] = useState(false);
  const toggleDept = () => setDeptModal((p) => !p);
  const [deptFormValues, setDeptFormValues] = useState([
    { DepartmentName: "", Description: "" },
  ]);
  const [deptPresent, setDeptPresent] = useState(false);
  const [deptSaving, setDeptSaving] = useState(false);

  // Column chooser
  const [chooserOpen, setChooserOpen] = useState(false);
  const addBufferRef = useRef([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // download hover
  const [downloadOpen, setDownloadOpen] = useState(false);

  /* ---------------- columns (WarehouseList pattern) ---------------- */
  const baseColumns = useMemo(
    () => [
      { key: "roleName", header: "ROLE NAME", get: (r) => r?.roleName },
      {
        key: "id",
        header: "ROLE ID",
        get: (r) => r?.id || r?._id,
      },
      { key: "desc", header: "DESCRIPTION", get: (r) => r?.desc },
      {
        key: "createdBy",
        header: "CREATED BY",
        get: (r) => r?.createdBy?.firstName || r?.createdBy?.name || "-",
      },
    ],
    []
  );

  const actionsColumn = useMemo(
    () => ({
      key: "__actions__",
      header: "ACTIONS",
      get: () => "",
      render: (r) => {
        const canEdit =
          (userData?._id &&
            String(userData?._id) === String(r?.createdBy?._id)) ||
          isMaster;
        return (
          <div className="action-chips">
            {permissions?.Edit && canEdit && (
              <span
                className="chip chip-edit"
                title="Edit"
                onClick={() => navigate(`/app/master/roles/edit/${r?._id}`)}
              >
                <FaPencilAlt size={14} color="white" />
              </span>
            )}
          </div>
        );
      },
    }),
    [permissions, isMaster, userData, navigate]
  );

  const colMap = useMemo(() => {
    const map = new Map();
    baseColumns.forEach((c) => map.set(c.key, c));
    map.set(actionsColumn.key, actionsColumn);
    return map;
  }, [baseColumns, actionsColumn]);

  const [selectedKeys, setSelectedKeys] = useState([]);
  useEffect(() => {
    // restore selection or default (like WarehouseList)
    const raw = localStorage.getItem("RoleListCols");
    let next = [];
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) {
        next = parsed.filter((k) => colMap.has(k));
      }
    } catch {}
    if (!next.length) next = baseColumns.map((c) => c.key);
    setSelectedKeys(next);
  }, [baseColumns, colMap]);

  const visibleCols = useMemo(
    () => [
      ...selectedKeys.map((k) => colMap.get(k)).filter(Boolean),
      actionsColumn,
    ],
    [selectedKeys, colMap, actionsColumn]
  );

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    const cols = visibleCols.filter((c) => c.key !== "__actions__");
    return rows.filter((r) =>
      cols.some((c) => toStr(c.get(r)).toLowerCase().includes(term))
    );
  }, [rows, search, visibleCols]);

  /* ---------------- data fetch ---------------- */
  const loadDepartments = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const url = `${List_Department}/${user?.database}`;
      const res = await _GetList(url);
      const active = (res?.Department || []).filter(
        (d) => d?.status === "Active"
      );
      if (active?.length) setDeptPresent(true);
      setDeptFormValues(
        active.map((d) => ({
          DepartmentName: d?.departmentName || "",
          Description: d?.departmentDesc || "",
          Id: d?._id,
        }))
      );
    } catch (e) {
      // silent
    }
  };

  const fetchRoles = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      setUserData(user);
      const inside = CheckPermission("Create User") || {};
      setPermissions((p) => ({ ...p, ...inside }));

      const master = user?.rolename?.roleName === "MASTER";
      setIsMaster(master);

      let res;
      if (master) {
        res = await _Get(Role_list_by_Master, user?._id);
      } else {
        res = await Get_RoleList(user?._id, user?.database);
      }
      const list = Array.isArray(res?.Role) ? res.Role : [];
      setRows(list);
    } catch (e) {
      console.error("[RoleList] fetch error:", e);
      setRows([]);
      setErrorMsg(
        e?.response?.data?.message || e?.message || "Unable to load roles."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      await loadDepartments();
      await fetchRoles();
    })();
  }, []);

  /* ---------------- column chooser modal ---------------- */
  const openChooser = () => {
    addBufferRef.current = [];
    setSelectedIndex(-1);
    setChooserOpen(true);
  };
  const closeChooser = () => setChooserOpen(false);

  const handleCheckbox = (checked, colKey) => {
    const buf = addBufferRef.current.slice();
    if (checked) {
      if (!buf.includes(colKey)) buf.push(colKey);
    } else {
      const i = buf.indexOf(colKey);
      if (i >= 0) buf.splice(i, 1);
    }
    addBufferRef.current = buf;
  };
  const addSelectedColumns = () => {
    const next = Array.from(
      new Set([...selectedKeys, ...addBufferRef.current])
    );
    setSelectedKeys(next);
  };
  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };
  const shiftUp = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };
  const shiftDown = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };
  const applyColumns = (e) => {
    e.preventDefault();
    localStorage.setItem("RoleListCols", JSON.stringify(selectedKeys));
    closeChooser();
  };

  /* ---------------- export ---------------- */
  const handleExportPDF = async () => {
    const cols = visibleCols.filter((c) => c.key !== "__actions__");
    const header = cols.map((c) => c.header).join(",");
    const lines = filteredRows.map((r) =>
      cols.map((c) => `"${toStr(c.get(r)).replace(/"/g, '""')}"`).join(",")
    );
    const csv = [header, ...lines].join("\r\n");
    await exportDataToPDF(csv, "RoleList");
  };

  const handleExportXLSX = async () => {
    // lazy import to avoid bundling if you don’t need it elsewhere
    const XLSX = await import("xlsx");
    const { saveAs } = await import("file-saver");
    const cols = visibleCols.filter((c) => c.key !== "__actions__");
    const data = filteredRows.map((r) => {
      const obj = {};
      cols.forEach((c) => (obj[c.header] = toStr(c.get(r))));
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `RoleList.xlsx`
    );
  };

  /* ---------------- departments modal handlers ---------------- */
  const handleDeptChange = (idx, e) => {
    const { name, value } = e.target;
    setDeptFormValues((prev) => {
      const next = prev.slice();
      next[idx] = { ...next[idx], [name]: value };
      return next;
    });
  };
  const addDeptRow = () =>
    setDeptFormValues((prev) => [
      ...prev,
      { DepartmentName: "", Description: "" },
    ]);

  const removeDeptRow = (row, idx) => {
    swal("Warning", "Sure You Want to Delete it", {
      buttons: { cancel: "cancel", catch: { text: "Delete", value: "delete" } },
    }).then((value) => {
      if (value === "delete") {
        if (row?.Id) {
          _Delete(Delete_Department, row.Id).catch(() => {});
        }
        setDeptFormValues((prev) => prev.filter((_, i) => i !== idx));
      }
    });
  };

  const submitDepartments = async (e) => {
    e.preventDefault();
    setDeptSaving(true);
    try {
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const payload = {
        Departments: deptFormValues.map((ele) => ({
          departmentName: ele?.DepartmentName,
          departmentDesc: ele?.Description,
          database: user?.database,
          departmentId: ele?.Id || null,
          created_by: user?._id,
        })),
      };
      await _PostSave(Create_Department, payload);
      swal("Departments Created");
      localStorage.setItem("CompanyDepartments", JSON.stringify(payload));
      toggleDept();
    } catch (err) {
      // silent
    } finally {
      setDeptSaving(false);
    }
  };

  /* ---------------- Loading ---------------- */
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const noData = !filteredRows?.length;

  return (
    <>
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      {/* ===== Toolbar (exact look & feel) ===== */}
      <Row className="rl-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            style={{
              paddingTop: 4,
              paddingBottom: 4,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              background: "#f4f7f6",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Role List{" "}
                <span className="count-pill" title="records">
                  {filteredRows.length}
                </span>
              </div>

              {/* Master → SuperAdmin picker (same behavior you had) */}
              <div style={{ flex: "1 1 120px" }} />
              {isMaster ? (
                <div style={{ minWidth: 280 }}>
                  <SuperAdminUI
                    onDropdownChange={(val) =>
                      localStorage.setItem(
                        "SuperadminIdByMaster",
                        JSON.stringify(val)
                      )
                    }
                    onSubmit={() => {
                      const raw = JSON.parse(
                        localStorage.getItem("SuperadminIdByMaster") || "null"
                      );
                      if (!raw) return;
                      const [id, db] = String(raw).split(" ");
                      // fetch roles for that super admin (non-master endpoint)
                      (async () => {
                        setLoading(true);
                        try {
                          const res = await Get_RoleList(id, db);
                          setRows(Array.isArray(res?.Role) ? res.Role : []);
                        } catch {
                          setRows([]);
                        } finally {
                          setLoading(false);
                        }
                      })();
                    }}
                  />
                </div>
              ) : null}

              {/* Search */}
              <div
                style={{
                  minWidth: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flex: "1 1 260px",
                }}
              >
                <Input
                  placeholder="Search role..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              {/* CSV/PDF/XLSX download like WarehouseList */}
              {permissions?.Download && (
                <div
                  onMouseEnter={() => setDownloadOpen(true)}
                  onMouseLeave={() => setDownloadOpen(false)}
                  style={{ position: "relative" }}
                >
                  <ImDownload
                    style={{ cursor: "pointer" }}
                    title="download file"
                    size={24}
                    color="rgb(8, 91, 245)"
                  />
                  {downloadOpen && (
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: 32,
                        zIndex: 5,
                        border: "1px solid rgb(8, 91, 245)",
                        backgroundColor: "white",
                        borderRadius: 10,
                        overflow: "hidden",
                        minWidth: 130,
                        boxShadow: "0 12px 24px rgba(0,0,0,.12)",
                      }}
                    >
                      <div
                        style={dlItemStyle}
                        onClick={handleExportPDF}
                        role="button"
                      >
                        .PDF
                      </div>
                      <div
                        style={dlItemStyle}
                        onClick={handleExportXLSX}
                        role="button"
                      >
                        .XLSX
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Column chooser */}
              <Button
                outline
                color="primary"
                onClick={openChooser}
                title="Filter Columns"
                style={{
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaFilter size={14} />
              </Button>

              {/* Actions on right like your old page */}
              {permissions?.Create && (
                <>
                  <Button
                    style={{
                      backgroundColor: "rgb(8, 91, 245)",
                      color: "white",
                      fontWeight: 600,
                      height: 32,
                      fontSize: 14,
                      textTransform: "uppercase",
                    }}
                    onClick={() => navigate("/app/master/roles/create")}
                  >
                    <FaPlus size={12} /> Role
                  </Button>
                </>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* ===== Table (same visual language) ===== */}
      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No roles found.
              </div>
            ) : (
              <div className="rl-table-wrap">
                <table className="rl-table">
                  <thead>
                    <tr>
                      {visibleCols.map((c) => (
                        <th key={c.key}>{c.header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r) => (
                      <tr key={r?._id || r?.id}>
                        {visibleCols.map((c) => (
                          <td key={c.key}>
                            {typeof c.render === "function"
                              ? c.render(r)
                              : toStr(c.get(r))}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* ===== Column Chooser Modal (same UX as WarehouseList) ===== */}
      <Modal
        isOpen={chooserOpen}
        toggle={() => setChooserOpen(false)}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={() => setChooserOpen(false)}>
          Change Fields
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h5>Available Columns</h5>
              <div style={listBoxStyle}>
                {baseColumns.map((col) => (
                  <div key={col.key} style={pickItemStyle}>
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          handleCheckbox(e.target.checked, col.key)
                        }
                      />
                      <span>{col.header}</span>
                    </label>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="8" md="8" sm="12" xl="8" xs="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div style={{ ...listBoxStyle, flex: 1 }}>
                  {selectedKeys.map((key, i) => {
                    const col = colMap.get(key);
                    if (!col) return null;
                    return (
                      <div
                        key={key}
                        style={{
                          ...pickItemStyle,
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                          cursor: "pointer",
                        }}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={shiftUp}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={shiftDown}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>

      {/* ===== Department Modal (kept from your original page, but styled lighter) ===== */}
      <Modal
        isOpen={deptModal}
        toggle={toggleDept}
        style={{ maxWidth: "1050px" }}
      >
        <ModalHeader toggle={toggleDept}>Add Department</ModalHeader>
        <ModalBody className="modalbodyhead">
          <div className="d-flex justify-content-center mb-2">
            <h3 style={{ fontWeight: 700, margin: 0 }}>
              Create Your Departments
            </h3>
          </div>

          <Form className="rl-form" onSubmit={submitDepartments}>
            {deptFormValues.map((row, idx) => (
              <Row key={idx} className="align-items-end">
                <Col lg="4" sm="6" md="4">
                  <FormGroup>
                    <Label className="mb-1">Create Department</Label>
                    <Input
                      required
                      placeholder="Department Name"
                      name="DepartmentName"
                      value={row.DepartmentName || ""}
                      onChange={(e) => handleDeptChange(idx, e)}
                    />
                  </FormGroup>
                </Col>
                <Col lg="4" sm="6" md="4">
                  <FormGroup>
                    <Label className="mb-1">Description</Label>
                    <Input
                      required
                      type="textarea"
                      rows={2}
                      placeholder="Department Description"
                      name="Description"
                      value={row.Description || ""}
                      onChange={(e) => handleDeptChange(idx, e)}
                    />
                  </FormGroup>
                </Col>
                <Col lg="4" sm="6" md="4">
                  <div className="d-flex gap-1">
                    {idx ? (
                      <Button
                        color="danger"
                        type="button"
                        onClick={() => removeDeptRow(row, idx)}
                      >
                        Remove
                      </Button>
                    ) : null}
                    <Button color="primary" type="button" onClick={addDeptRow}>
                      Add
                    </Button>
                  </div>
                </Col>
              </Row>
            ))}

            <div className="d-flex justify-content-center mt-1">
              <Button type="submit" color="primary" disabled={deptSaving}>
                {deptSaving ? "Submitting..." : "Submit"}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </>
  );
}

/* —— tiny styles reused from WarehouseList —— */
const dlItemStyle = {
  padding: "10px 12px",
  cursor: "pointer",
  fontWeight: 600,
  letterSpacing: ".02em",
};
const listBoxStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  maxHeight: 280,
  overflow: "auto",
  background: "#fff",
  padding: 8,
};
const pickItemStyle = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  marginBottom: 6,
  background: "#fff",
};
